import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  Stack,
  StackSeparator,
  Spinner,
  Text,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import QuickSearchForm from '@/components/ui/quick-search-form';
import BrowseContent from './browse-content';
import useWorkspace from '@/lib/hooks/use-workspace';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { ExtendedWorkspace, ContentType } from '@/lib/types';
import api from '@/utils/api';

interface Type extends ContentType {
  content_counts: number;
  workspace_content_type_id: number;
  workspace_content_type_reference_id: string;
}

interface Workspace extends ExtendedWorkspace {
  public_workspace_id: number | null;
  checked: boolean;
  types: Type[];
}

interface BrowseTypeProps {
  id: number;
  name: string;
  reference_id: string;
}

const AVATAR_PALETTES = ['blue', 'green', 'orange', 'purple', 'cyan', 'pink', 'yellow', 'red'];

function paletteFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
}

export default function PartnerContent() {
  const contentDrawerModal = useModalToggle();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<Workspace[]>([]);
  const [loadingItems, setLoadingItems] = useState<{ [key: number]: boolean }>({});
  const [selectedItem, setSelectedItem] = useState<Workspace>();
  const [selectedTypes, setSelectedTypes] = useState<BrowseTypeProps[]>([]);
  const { workspace } = useWorkspace();

  const fetchData = useCallback(
    async (searchTerm = '') => {
      try {
        setIsLoading(true);
        const resWorkspace = await api.get(
          `workspace/public/${workspace.sport_type_id}?search_term=${searchTerm}`,
        );
        const resSelections = await api.get(`public-workspace-selections/${workspace.id}`);
        const selectedWorkspaces = resSelections.data.data;

        const workspaces = resWorkspace.data.data.map(
          (data: { workspace: Workspace; content_types: ContentType[] }) => {
            const selection = selectedWorkspaces.find(
              (sel: { public_workspace_id: number }) =>
                sel.public_workspace_id === data.workspace.id,
            );
            return {
              ...data.workspace,
              public_workspace_id: selection ? selection.id : null,
              checked: !!selection,
              types: [...data.content_types],
            };
          },
        );
        const filteredItems = workspaces.filter((item: Workspace) => item.id !== workspace.id);
        setItems(filteredItems.sort((a: Workspace, b: Workspace) => a.name.localeCompare(b.name)));
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [workspace],
  );

  const handleSearch = (search: string) => {
    fetchData(search);
  };

  useEffect(() => {
    if (workspace && workspace.sport_type_id) {
      fetchData();
    }
  }, [fetchData, workspace]);

  const togglePartner = async (item: Workspace, show: boolean) => {
    if (item.checked === show) return;
    setLoadingItems((prev) => ({ ...prev, [item.id]: true }));
    try {
      if (show) {
        await api.post(`public-workspace-selections`, {
          workspace_id: workspace.id,
          public_workspace_id: item.id,
        });
        toaster.create({
          type: 'success',
          title: 'Partner library shown',
        });
      } else {
        await api.delete(`public-workspace-selections/${item.public_workspace_id}`);
        toaster.create({
          type: 'success',
          title: 'Partner library hidden',
        });
      }
      fetchData();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update partner library',
      });
    } finally {
      setLoadingItems((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleBrowseContent = (item: Workspace) => {
    const types = item.types
      .filter((type) => type.content_counts > 0)
      .map((type) => ({
        id: type.id,
        name: type.name,
        reference_id: type.workspace_content_type_reference_id || type.reference_id,
      }));
    const filteredTypes = types.filter(
      (type) => type.reference_id !== undefined && type.reference_id !== null,
    );
    setSelectedTypes(filteredTypes);
    setSelectedItem(item);
    contentDrawerModal.open();
  };

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title>Partner content libraries</Card.Title>
        <Card.Description>
          Choose which partners&apos; content libraries appear in your workspace. Browse a
          partner&apos;s content before deciding.
        </Card.Description>
      </Card.Header>
      <Card.Body>
        <Stack gap={5}>
          <QuickSearchForm handleSearch={handleSearch} placeholder='Search partners...' />
          {isLoading ? (
            <Stack flex='1'>
              <Skeleton variant='shine' height='14' />
              <Skeleton variant='shine' height='14' />
              <Skeleton variant='shine' height='14' />
            </Stack>
          ) : items.length === 0 ? (
            <Text fontSize='sm' color='fg.muted' py={6} textAlign='center'>
              No partner workspaces found.
            </Text>
          ) : (
            <Stack gap={4} separator={<StackSeparator />}>
              {items.map((item) => {
                const palette = paletteFor(item.name || 'partner');
                const isItemLoading = !!loadingItems[item.id];
                return (
                  <Flex
                    key={item.id}
                    align='center'
                    gap={4}
                    direction={{ base: 'column', md: 'row' }}
                  >
                    <HStack flex={1} gap={3} align='center' w='full'>
                      <Avatar
                        name={item.name}
                        size='md'
                        colorPalette={palette}
                        variant='subtle'
                      />
                      <Stack gap={0} flex={1}>
                        <Text fontSize='sm' fontWeight='semibold'>
                          {item.name}
                        </Text>
                        {item.description && (
                          <Text fontSize='xs' color='fg.muted'>
                            {item.description}
                          </Text>
                        )}
                      </Stack>
                    </HStack>
                    <HStack gap={2} flexShrink={0}>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => handleBrowseContent(item)}
                      >
                        Browse content
                      </Button>
                      <HStack
                        gap={0}
                        borderWidth={1}
                        borderColor='border'
                        borderRadius='md'
                        overflow='hidden'
                      >
                        <Button
                          type='button'
                          size='sm'
                          variant={item.checked ? 'subtle' : 'ghost'}
                          colorPalette={item.checked ? 'green' : 'gray'}
                          borderRadius={0}
                          onClick={() => togglePartner(item, true)}
                          disabled={isItemLoading}
                          aria-pressed={item.checked}
                        >
                          {isItemLoading && item.checked ? (
                            <Spinner size='xs' />
                          ) : (
                            <Icon as={EyeIcon} h={4} w={4} />
                          )}
                          Show
                        </Button>
                        <Box w='1px' h='100%' bg='border' alignSelf='stretch' />
                        <Button
                          type='button'
                          size='sm'
                          variant={!item.checked ? 'subtle' : 'ghost'}
                          borderRadius={0}
                          onClick={() => togglePartner(item, false)}
                          disabled={isItemLoading}
                          aria-pressed={!item.checked}
                        >
                          {isItemLoading && !item.checked ? (
                            <Spinner size='xs' />
                          ) : (
                            <Icon as={EyeSlashIcon} h={4} w={4} />
                          )}
                          Hide
                        </Button>
                      </HStack>
                    </HStack>
                  </Flex>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card.Body>

      {selectedItem && (
        <BrowseContent
          {...contentDrawerModal.props}
          workspace={selectedItem}
          types={selectedTypes}
        />
      )}
    </Card.Root>
  );
}
